import { useMemo, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import C from '../../constants/palette';
import StoneVisual from '../shared/StoneVisual';
import SiteFooter from '../shared/SiteFooter';

const VALUE_FILTERS = [
  ['ALL', 'All'],
  ['LT10K', '<10K'],
  ['10K_20K', '10K-20K'],
  ['20K_30K', '20K-30K'],
];

const COLLECTION_PAGE_SIZE = 24;

function navigate(path) {
  window.history.pushState(null, '', path);
  window.dispatchEvent(new Event('popstate'));
  window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
}

function Header({ compact = false }) {
  return (
    <header style={{
      position: 'sticky', top: 0, zIndex: 100,
      background: '#F8F9FAf8',
      backdropFilter: 'blur(12px)',
      borderBottom: `1px solid ${C.border}`,
      padding: compact ? '0 32px' : '0 48px',
      height: 64,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    }}>
      <button
        onClick={() => navigate('/')}
        style={{
          border: 'none', background: 'none', padding: 0, cursor: 'pointer',
          display: 'flex', alignItems: 'baseline', gap: 10,
        }}
      >
        <span style={{ fontFamily: 'Playfair Display', fontSize: 22, fontWeight: 500, color: C.ink, letterSpacing: '0.04em' }}>
          KINGSTRADE
        </span>
        <span style={{ fontSize: 9, color: C.muted, letterSpacing: '0.18em', fontWeight: 500 }}>
          TREASURE COLLECTION
        </span>
      </button>
      <nav style={{ display: 'flex', gap: 28, alignItems: 'center' }}>
        <button
          onClick={() => navigate('/collection')}
          style={{ border: 'none', background: 'none', fontSize: 11, letterSpacing: '0.16em', color: C.muted, fontWeight: 600, cursor: 'pointer' }}
        >
          EXPLORE FULL CATALOGUE
        </button>
      </nav>
    </header>
  );
}

function matchesFilter(stone, filter) {
  if (filter === 'ALL') return true;
  if (filter === 'LT10K') return stone.price < 10000;
  if (filter === '10K_20K') return stone.price >= 10000 && stone.price < 20000;
  if (filter === '20K_30K') return stone.price >= 20000 && stone.price < 30000;
  return true;
}

function filterTreasures(stones, query, filter) {
  const q = query.trim().toLowerCase();
  return stones.filter(stone => {
    const matchesQuery = !q ||
      stone.id?.toLowerCase().includes(q) ||
      stone.name?.toLowerCase().includes(q) ||
      stone.author?.toLowerCase().includes(q);
    return matchesQuery && matchesFilter(stone, filter);
  });
}

function formatMoney(value) {
  return `$${Number(value || 0).toLocaleString()}`;
}

function topAuthors(stones) {
  const counts = stones.reduce((acc, stone) => {
    const author = stone.author?.trim();
    if (!author) return acc;
    acc[author] = (acc[author] || 0) + 1;
    return acc;
  }, {});

  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4)
    .map(([author, count]) => ({ author, count }));
}

function TreasureCard({ stone, onBuy, variant = 'compact' }) {
  const [hovered, setHovered] = useState(false);
  const imageHeight = variant === 'featured' ? 172 : 136;

  return (
    <article
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => stone.status === 'AVAILABLE' && onBuy(stone)}
      style={{
        background: '#fff',
        border: `1px solid ${C.border}`,
        cursor: stone.status === 'AVAILABLE' ? 'pointer' : 'default',
        transition: 'transform 0.18s ease, border-color 0.18s ease',
        transform: hovered && stone.status === 'AVAILABLE' ? 'translateY(-2px)' : 'translateY(0)',
        borderColor: hovered ? '#d8dde5' : C.border,
        overflow: 'hidden',
      }}
    >
      <div style={{
        height: imageHeight,
        background: stone.bg,
        overflow: 'hidden',
        position: 'relative',
      }}>
        <StoneVisual stone={stone} size={imageHeight} />
        {stone.status !== 'AVAILABLE' && (
          <div style={{
            position: 'absolute', inset: 0,
            background: 'rgba(15,23,42,0.58)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ color: '#fff', fontSize: 10, letterSpacing: '0.18em', fontWeight: 700 }}>
              SOLD
            </span>
          </div>
        )}
      </div>
      <div style={{ padding: variant === 'featured' ? '16px 18px 18px' : '14px 16px 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
          <span style={{ fontSize: 9, color: C.gold, letterSpacing: '0.14em', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {stone.author || 'UNKNOWN'}
          </span>
          <span style={{ fontSize: 8, color: C.muted, letterSpacing: '0.1em', flexShrink: 0 }}>{stone.id}</span>
        </div>
        <h3 style={{
          fontFamily: 'Playfair Display',
          fontSize: variant === 'featured' ? 17 : 15,
          fontWeight: 400,
          color: C.ink,
          lineHeight: 1.32,
          minHeight: variant === 'featured' ? 44 : 40,
          marginBottom: 12,
        }}>
          {stone.name}
        </h3>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontFamily: 'DM Mono', fontSize: variant === 'featured' ? 13 : 12, color: C.ink, fontWeight: 600 }}>
            {formatMoney(stone.price)}
          </span>
          <span style={{ fontSize: 8, color: C.muted, letterSpacing: '0.12em' }}>ESTIMATE</span>
        </div>
      </div>
    </article>
  );
}

function SearchBox({ value, onChange, onSubmit, autoFocus = false }) {
  return (
    <form onSubmit={onSubmit} style={{ width: '100%', maxWidth: 720, margin: '0 auto' }}>
      <div style={{
        height: 58,
        border: `1px solid ${C.border}`,
        background: '#fff',
        display: 'flex',
        alignItems: 'center',
        padding: '0 20px',
        boxShadow: '0 12px 30px rgba(15,23,42,0.06)',
      }}>
        <span style={{ color: C.muted, fontSize: 18, marginRight: 14 }}>⌕</span>
        <input
          autoFocus={autoFocus}
          type="search"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder="Enter masterpiece, master artisan, institution, or catalog ID..."
          style={{
            width: '100%',
            border: 'none',
            outline: 'none',
            fontSize: 16,
            color: C.ink,
            background: 'transparent',
          }}
        />
      </div>
    </form>
  );
}

function LandingPage({ stones, onBuy }) {
  const [query, setQuery] = useState('');
  const featured = useMemo(
    () => [...stones].sort((a, b) => b.price - a.price).slice(0, 6),
    [stones]
  );
  const heroItems = featured.slice(0, 3);
  const authorHighlights = useMemo(() => topAuthors(stones), [stones]);

  const submitSearch = (e) => {
    e.preventDefault();
    const suffix = query.trim() ? `?q=${encodeURIComponent(query.trim())}` : '';
    navigate(`/collection${suffix}`);
  };

  return (
    <div style={{ minHeight: '100vh', background: C.bg }}>
      <Header />
      <main>
        <section style={{
          padding: '72px 48px 56px',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 360px), 1fr))',
          gap: 48,
          alignItems: 'center',
          maxWidth: 1280,
          margin: '0 auto',
        }}>
          <div>
            <p style={{ fontSize: 10, letterSpacing: '0.28em', color: C.gold, fontWeight: 700, marginBottom: 22 }}>
              珍寶典藏 / TREASURE COLLECTION
            </p>
            <h1 style={{
              fontFamily: 'Playfair Display',
              fontSize: 72,
              fontWeight: 400,
              color: C.ink,
              lineHeight: 1.03,
              marginBottom: 24,
            }}>
              Kingstrade<br /><em>Treasure Archive.</em>
            </h1>
            <p style={{ fontSize: 16, color: C.muted, maxWidth: 560, margin: '0 0 32px', lineHeight: 1.85, fontWeight: 300 }}>
              An exclusive sanctuary of cultural heritage and institutional-grade antiquities. Curated for the world's most discerning collectors.
            </p>
            <SearchBox value={query} onChange={setQuery} onSubmit={submitSearch} />
            <div style={{ marginTop: 22, display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => navigate('/collection')}
                style={{
                  padding: '12px 28px',
                  background: C.gold,
                  border: 'none',
                  color: C.dark,
                  fontSize: 11,
                  letterSpacing: '0.16em',
                  fontWeight: 800,
                  cursor: 'pointer',
                }}
              >
                EXPLORE COLLECTION
              </button>
              <button
                onClick={() => navigate('/collection?filter=20K_30K')}
                style={{
                  padding: '12px 0',
                  border: 'none',
                  background: 'transparent',
                  color: C.muted,
                  fontSize: 11,
                  letterSpacing: '0.14em',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                VIEW TOP VALUATIONS
              </button>
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 0.82fr',
            gap: 14,
            alignItems: 'stretch',
          }}>
            {heroItems.map((stone, index) => (
              <button
                key={stone.id}
                onClick={() => stone.status === 'AVAILABLE' && onBuy(stone)}
                style={{
                  border: `1px solid ${C.border}`,
                  background: '#fff',
                  padding: 0,
                  cursor: stone.status === 'AVAILABLE' ? 'pointer' : 'default',
                  textAlign: 'left',
                  minHeight: index === 0 ? 368 : 176,
                  gridRow: index === 0 ? 'span 2' : 'auto',
                  overflow: 'hidden',
                }}
              >
                <div style={{ height: index === 0 ? 238 : 108, background: stone.bg, overflow: 'hidden' }}>
                  <StoneVisual stone={stone} size={index === 0 ? 238 : 108} />
                </div>
                <div style={{ padding: index === 0 ? 18 : 14 }}>
                  <p style={{ fontSize: 8, color: C.gold, letterSpacing: '0.14em', fontWeight: 700, marginBottom: 8 }}>
                    {stone.author || 'UNKNOWN'}
                  </p>
                  <h2 style={{
                    fontFamily: 'Playfair Display',
                    fontSize: index === 0 ? 22 : 15,
                    lineHeight: 1.25,
                    fontWeight: 400,
                    color: C.ink,
                    marginBottom: 10,
                  }}>
                    {stone.name}
                  </h2>
                  <p style={{ fontFamily: 'DM Mono', fontSize: 12, color: C.ink }}>
                    {formatMoney(stone.price)}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </section>

        <section style={{
          borderTop: `1px solid ${C.border}`,
          borderBottom: `1px solid ${C.border}`,
          background: '#fff',
        }}>
          <div style={{
            maxWidth: 1280,
            margin: '0 auto',
            padding: '28px 48px',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
            gap: 18,
          }}>
            {[
              ['Curated Masterpieces', `${stones.length.toLocaleString()} pieces strictly curated worldwide`],
              ['Dimensions of History', 'Spanning dynasties, kilns & imperial artisans'],
              ['Authoritative Valuation', 'Top-tier auction grade selections'],
              ['Immersive Appreciation', 'Microscopic high-definition details'],
            ].map(([title, description]) => (
              <div key={title}>
                <p style={{ fontFamily: 'Playfair Display', fontSize: 22, color: C.ink, fontWeight: 400, lineHeight: 1.2, marginBottom: 8 }}>{title}</p>
                <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.55 }}>{description}</p>
              </div>
            ))}
          </div>
        </section>

        <section style={{
          maxWidth: 1280,
          margin: '0 auto',
          padding: '64px 48px 34px',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
          gap: 44,
          alignItems: 'start',
        }}>
          <div>
            <p style={{ fontSize: 10, color: C.gold, letterSpacing: '0.2em', fontWeight: 700, marginBottom: 14 }}>CURATORIAL NOTE</p>
            <h2 style={{ fontFamily: 'Playfair Display', fontSize: 42, lineHeight: 1.16, fontWeight: 400, color: C.ink, marginBottom: 18 }}>
              Curated for the discerning collector: Discover, Appreciate, and Acquire.
            </h2>
            <p style={{ color: C.muted, fontSize: 15, lineHeight: 1.85, fontWeight: 300 }}>
              We eliminate distractions, letting the art speak for itself. Enjoy high-definition imagery and authoritative valuations in an undisturbed, exclusive environment. Click to enter microscopic viewing mode and access your private bespoke acquisition service at any time.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
            <button onClick={() => navigate('/collection?q=中国长城文创大赛')} style={{ border: `1px solid ${C.border}`, background: '#fff', padding: 20, textAlign: 'left', cursor: 'pointer' }}>
              <p style={{ fontSize: 9, color: C.gold, letterSpacing: '0.16em', fontWeight: 700, marginBottom: 16 }}>MUSEUM-GRADE</p>
              <h3 style={{ fontFamily: 'Playfair Display', fontSize: 23, lineHeight: 1.25, color: C.ink, fontWeight: 400, marginBottom: 24 }}>Explore Heritage Masterpieces</h3>
              <span style={{ fontSize: 10, color: C.muted, letterSpacing: '0.14em', fontWeight: 700 }}>IMPECCABLE PROVENANCE</span>
            </button>
            <button onClick={() => navigate('/collection?filter=20K_30K')} style={{ border: `1px solid ${C.border}`, background: '#fff', padding: 20, textAlign: 'left', cursor: 'pointer' }}>
              <p style={{ fontSize: 9, color: C.gold, letterSpacing: '0.16em', fontWeight: 700, marginBottom: 16 }}>AUTHORITATIVE VALUATION</p>
              <h3 style={{ fontFamily: 'Playfair Display', fontSize: 23, lineHeight: 1.25, color: C.ink, fontWeight: 400, marginBottom: 24 }}>Focus on Major Masterpieces</h3>
              <span style={{ fontSize: 10, color: C.muted, letterSpacing: '0.14em', fontWeight: 700 }}>UNDER-30K CURATED TIER</span>
            </button>
          </div>
        </section>

        <section style={{ padding: '30px 48px 78px', maxWidth: 1280, margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 22 }}>
            <div>
              <p style={{ fontSize: 10, color: C.gold, letterSpacing: '0.2em', fontWeight: 700, marginBottom: 8 }}>CURATED MASTERPIECES</p>
              <h2 style={{ fontFamily: 'Playfair Display', fontSize: 34, fontWeight: 400, color: C.ink }}>The Pinnacle of Value: Heritage Masterpieces</h2>
            </div>
            <button onClick={() => navigate('/collection')} style={{ border: 'none', background: 'none', color: C.muted, fontSize: 11, letterSpacing: '0.14em', cursor: 'pointer' }}>
              EXPLORE FULL CATALOGUE →
            </button>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
            gap: 18,
          }}>
            {featured.map(stone => <TreasureCard key={stone.id} stone={stone} onBuy={onBuy} variant="featured" />)}
          </div>
        </section>

        <section style={{
          background: C.dark,
          color: '#fff',
          padding: '52px 48px',
        }}>
          <div style={{
            maxWidth: 1280,
            margin: '0 auto',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))',
            gap: 38,
            alignItems: 'center',
          }}>
            <div>
              <p style={{ fontSize: 10, color: C.gold, letterSpacing: '0.2em', fontWeight: 700, marginBottom: 12 }}>CURATED DIRECTORY</p>
              <h2 style={{ fontFamily: 'Playfair Display', fontSize: 34, fontWeight: 400, lineHeight: 1.2 }}>
                Discover masterpieces by provenance, master artisans, or institutional collections.
              </h2>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {authorHighlights.map(({ author, count }) => (
                <button
                  key={author}
                  onClick={() => navigate(`/collection?q=${encodeURIComponent(author)}`)}
                  style={{
                    border: '1px solid rgba(255,255,255,0.18)',
                    background: 'rgba(255,255,255,0.06)',
                    color: '#fff',
                    padding: '12px 14px',
                    cursor: 'pointer',
                    minWidth: 150,
                    textAlign: 'left',
                  }}
                >
                  <span style={{ display: 'block', fontSize: 13, marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{author}</span>
                  <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.62)', letterSpacing: '0.14em' }}>{count} MASTERPIECES</span>
                </button>
              ))}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function CollectionSearchPage({ stones, onBuy }) {
  const params = new URLSearchParams(window.location.search);
  const initialQuery = params.get('q') || '';
  const initialFilter = VALUE_FILTERS.some(([key]) => key === params.get('filter')) ? params.get('filter') : 'ALL';
  const [query, setQuery] = useState(initialQuery);
  const [filter, setFilter] = useState(initialFilter);
  const [visibleCount, setVisibleCount] = useState(COLLECTION_PAGE_SIZE);
  const loadMoreRef = useRef(null);

  useEffect(() => {
    const queryPart = query.trim() ? `q=${encodeURIComponent(query.trim())}` : '';
    const filterPart = filter !== 'ALL' ? `filter=${encodeURIComponent(filter)}` : '';
    const suffix = [queryPart, filterPart].filter(Boolean).join('&');
    const next = suffix ? `/collection?${suffix}` : '/collection';
    window.history.replaceState(null, '', next);
  }, [query, filter]);

  const results = useMemo(() => filterTreasures(stones, query, filter), [stones, query, filter]);
  const visibleResults = useMemo(() => results.slice(0, visibleCount), [results, visibleCount]);
  const hasMoreResults = visibleCount < results.length;

  useEffect(() => {
    setVisibleCount(COLLECTION_PAGE_SIZE);
  }, [query, filter, results.length]);

  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node || !hasMoreResults) return undefined;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVisibleCount(count => Math.min(count + COLLECTION_PAGE_SIZE, results.length));
      }
    }, { rootMargin: '120px 0px 120px 0px' });

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMoreResults, results.length, visibleCount]);

  return (
    <div style={{ minHeight: '100vh', background: C.bg }}>
      <Header compact />
      <main style={{ padding: '56px 48px 72px' }}>
        <section style={{ textAlign: 'center', marginBottom: 30 }}>
          <p style={{ fontSize: 10, letterSpacing: '0.26em', color: C.gold, fontWeight: 700, marginBottom: 16 }}>
            CURATED CATALOGUE
          </p>
          <h1 style={{ fontFamily: 'Playfair Display', fontSize: 44, fontWeight: 400, color: C.ink, marginBottom: 26 }}>
            Discover Heritage Masterpieces
          </h1>
          <SearchBox value={query} onChange={setQuery} onSubmit={e => e.preventDefault()} autoFocus />
          <p style={{ marginTop: 18, marginBottom: 10, fontSize: 9, color: C.muted, letterSpacing: '0.18em', fontWeight: 700 }}>
            VALUATION TIERS
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
            {VALUE_FILTERS.map(([key, label]) => {
              const active = filter === key;
              return (
                <button
                  key={key}
                  onClick={() => setFilter(key)}
                  style={{
                    padding: '7px 14px',
                    border: `1px solid ${active ? C.gold : C.border}`,
                    background: active ? C.gold : 'transparent',
                    color: active ? C.dark : C.muted,
                    fontSize: 10,
                    letterSpacing: '0.12em',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </section>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <p style={{ fontSize: 12, color: C.muted }}>
            Presenting {visibleResults.length} of {results.length} curated pieces{query.trim() ? ` for "${query.trim()}"` : ''}
          </p>
          <button onClick={() => navigate('/')} style={{ border: 'none', background: 'none', color: C.muted, fontSize: 11, letterSpacing: '0.12em', cursor: 'pointer' }}>
            ← BACK TO HOME
          </button>
        </div>

        {results.length ? (
          <>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
              gap: 14,
            }}>
              {visibleResults.map(stone => <TreasureCard key={stone.id} stone={stone} onBuy={onBuy} />)}
            </div>
            <div
              ref={loadMoreRef}
              style={{
                height: 88,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: C.muted,
                fontSize: 10,
                letterSpacing: '0.16em',
                fontWeight: 700,
              }}
            >
              {hasMoreResults ? 'LOADING MORE MASTERPIECES...' : 'END OF CATALOGUE'}
            </div>
          </>
        ) : (
          <div style={{ padding: '72px 0', textAlign: 'center', borderTop: `1px solid ${C.border}` }}>
            <p style={{ fontFamily: 'Playfair Display', fontSize: 28, color: C.muted, fontWeight: 400, marginBottom: 10 }}>No matching treasures</p>
            <p style={{ fontSize: 13, color: C.muted }}>Try a creator name, institution, title, or catalog ID.</p>
          </div>
        )}
      </main>
    </div>
  );
}

export default function Storefront({ onBuy, mode = 'landing' }) {
  const [stones, setStones] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get('/api/stones')
      .then(res => setStones(res.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ fontSize: 11, letterSpacing: '0.22em', color: C.muted, fontWeight: 500 }}>LOADING COLLECTION...</p>
      </div>
    );
  }

  if (mode === 'collection') {
    return <CollectionSearchPage stones={stones} onBuy={onBuy} />;
  }

  return <LandingPage stones={stones} onBuy={onBuy} />;
}
